import $ from "jquery";
import ICLICommand from "flow/interfaces/ICLICommand";
import { Dictionary } from "flow/api/types";

function isStringStartOrEnd(character: string) {
    return character === '"' || character === "'";
}

function isEmptyToken(token: string) {
    return token === "" || token === " ";
}

function getTokens(characters: string) {
    const tokens: string[] = [];
    let partialToken = "";

    for (let i = 0; i < characters.length; ++i) {
        const currentChar = characters[i];

        if (currentChar === " " || currentChar === "=") {
            if (!isEmptyToken(partialToken)) {
                tokens.push(partialToken);
            }

            partialToken = "";
        } else if (isStringStartOrEnd(currentChar)) {
            // End prior token if any
            if (!isEmptyToken(partialToken)) {
                tokens.push(partialToken);
            }

            partialToken = "";

            ++i;
            // Every token that we encounter belongs to the string until we encounter the
            // string-token again. Must end with same string character as it started.
            while (i < characters.length && currentChar !== characters[i]) {
                partialToken += characters[i++];
            }

            tokens.push(partialToken);
            partialToken = "";
        } else {
            partialToken += currentChar;
        }
    }

    if (partialToken !== "" && partialToken !== " ") {
        tokens.push(partialToken);
    }

    return tokens;
}

function getOption(command: ICLICommand, optionName: string) {
    const isLong = optionName.indexOf("--") === 0;
    optionName = isLong ? optionName.substring(2) : optionName.substring(1);

    for (let i = 0; i < command.options.length; ++i) {
        if (isLong && command.options[i].name === optionName) {
            return command.options[i];
        } else if (!isLong && command.options[i].shortName === optionName) {
            return command.options[i];
        }
    }
    return null;
}

function consumeOption(command: ICLICommand, remainingTokens: string[]) {
    const firstToken = remainingTokens[0];

    const option = getOption(command, firstToken);

    if (option) {
        if (option.alreadyCovered) {
            throw new Error("Option " + option.name + " specified more than once.");
        }

        let result: Dictionary;
        option.alreadyCovered = true;
        remainingTokens.shift();

        if (option.hasArgument) {
            if (remainingTokens.length === 0) {
                throw new Error("Option " + option.name + " requires an argument.");
            }

            let value: any = remainingTokens.shift();

            if (option.type) {
                switch (option.type) {
                    case "string":
                        // Validation already covered by mandatory check above. :)
                        break;
                    case "number": {
                        const valueNumber = parseInt(value);
                        if (isNaN(valueNumber)) {
                            throw new Error("Option " + option.name + " only takes a number");
                        }
                        value = valueNumber;
                        break;
                    }
                    case "boolean":
                        if (value !== "true" && value !== "false") {
                            throw new Error("Option " + option.name + " only takes a boolean");
                        }
                        value = value === "true";
                        break;
                }
            }

            result = consumeTokens(command, remainingTokens);
            result[option.name] = value;
        } else {
            result = consumeTokens(command, remainingTokens);
            result[option.name] = true;
        }

        return result;
    } else {
        throw new Error("Unknown option " + firstToken);
    }
}

function getNextArgument(args: Dictionary) {
    for (let i = 0; i < args.length; ++i) {
        if (!args[i].alreadyCovered) {
            return args[i];
        }
    }
    return null;
}

function consumeArgument(command: ICLICommand, remainingTokens: string[]) {
    const argument = getNextArgument(command.arguments);
    let result: Dictionary;

    if (argument && remainingTokens.length > 0) {
        argument.alreadyCovered = true;
        const value = remainingTokens.shift();

        result = consumeArgument(command, remainingTokens);
        result[argument.name] = value;

        return result;
    } else if (argument === null || argument === undefined) {
        if (remainingTokens.length > 0) {
            throw new Error("Too many arguments.");
        }
        return {};
    } else if (argument.mandatory) {
        throw new Error("Missing argument " + argument.name);
    } else {
        result = {};
        result[argument.name] = undefined;
        return result;
    }
}

function consumeTokens(command: ICLICommand, remainingTokens: string[]) {
    if (remainingTokens.length === 0) {
        const argument = getNextArgument(command.arguments);
        if (argument != null && argument.mandatory) {
            throw new Error("Missing argument " + argument.name);
        }
        return getOptionsDefaultValues(command);
    }

    const firstToken = remainingTokens[0];

    if (firstToken.indexOf("-") === 0) {
        return consumeOption(command, remainingTokens);
    }

    return consumeArgument(command, remainingTokens);
}

function getOptionsDefaultValues(command: ICLICommand) {
    const defaults: Dictionary = {};
    for (let i = 0; i < command.options.length; ++i) {
        if (command.options[i].defaultValue) {
            defaults[command.options[i].name] = command.options[i].defaultValue;
        }
    }
    return defaults;
}

function parseOptsAndArgs(command: ICLICommand, commandLine: string) {
    //if (commandLine.indexOf(' ') === -1) {
    //    return {};
    //}

    const tokens = getTokens(commandLine);

    // Warning, first argument true makes it a deep copy, so can't be directly replaced by Object.assign
    const commandCopy = $.extend(true, {}, command);

    tokens.shift(); // Remove the command itself

    const input = consumeTokens(commandCopy, tokens);

    return input;
}

function getCommandName(commandLine: string) {
    if (commandLine.indexOf(" ") >= 0) {
        return commandLine.substring(0, commandLine.indexOf(" "));
    }
    return commandLine;
}

export default {
    parseOptsAndArgs: parseOptsAndArgs,
    getCommandName: getCommandName
};
